import type { ReportNavPreferenceResponse, StarGridNavGroupPreference } from 'src/components/star-grid/navigation.types';
import { ReportDefinitionApiError } from 'src/components/star-grid/utils/reportDefinitionError';
import { loadPnlVersions } from 'src/components/star-grid/services/loadPnlVersions';
import { apiErrorDetail, apiRequest, authedFetch, deleteResource, getJson, sendJson } from 'src/components/star-grid/services/starGridHttp';
import type { ReportPrivateConfigResponse } from 'src/components/star-grid/reportPrivateConfig.types';
import { parseReportPrivateConfig } from 'src/components/star-grid/utils/reportPrivateConfigSchema';
import { CATALOG_MAX_AGE_MS, PNL_VERSION_PAGE_SIZE, STAR_GRID_API_BASE_URL } from 'src/components/star-grid/starGridConstants';
import { PnlDataVersion, PnlDeckGroup } from '../reportSelectors.types';
import {
  CreateSnapshotTagRequest,
  CreatedSnapshotTag,
  PnlAliasLabels,
  PnlHierarchyAliases,
  PnlHierarchyTree,
  PnlReportConfig,
  PnlReportSummary,
  PnlSaveSubsetViewRequest,
  PnlSubsetView,
  PnlSubsetViewSummary,
  SnapshotTagList,
  StarGridHealth,
  StarGridInfra
} from '../starGridTypes';

/**
 * How long a page reuses the report list, the nav preference and a hierarchy tree it just read. Long
 * enough to collapse the repeats of one page load; any write clears them sooner (see `getJson`).
 */
const REPEAT_READ_MAX_AGE_MS = 30_000;
/** The version list changes only when a new snapshot lands (hours apart); every report open re-read all 1,600. */
const VERSIONS_MAX_AGE_MS = 5 * 60_000;

/** Unauthenticated liveness probe. */
export async function getStarGridHealth(): Promise<StarGridHealth> {
  const response = await fetch(`${STAR_GRID_API_BASE_URL}/health`, {
    method: 'GET',
    cache: 'no-store',
    headers: { Accept: 'application/json' }
  });

  if (!response.ok) {
    throw new Error(`StarGrid API health check failed: ${response.status} ${response.statusText}`);
  }

  return (await response.json()) as StarGridHealth;
}

/** Auth-gated StarRocks infrastructure descriptor (API reads it from SSM). */
export async function getStarGridInfra(): Promise<StarGridInfra> {
  return getJson<StarGridInfra>('/infra');
}

// ─── PnL Reports (persisted) ──────────────────────────────────────────────────

/**
 * The whole hierarchy as a nested member tree. Uncapped: the subset editor's Keep/Leaves operations are
 * set algebra over it, so a truncated tree would answer them wrongly.
 */
export async function getHierarchyTree(hierarchyId: string, signal?: AbortSignal): Promise<PnlHierarchyTree> {
  const path = `/pnl-hierarchies/${encodeURIComponent(hierarchyId)}/tree`;
  const response = await authedFetch(path, { method: 'GET', signal });
  if (!response.ok) throw new ReportDefinitionApiError(response.status, await apiErrorDetail(response, path));
  return (await response.json()) as PnlHierarchyTree;
}

/** Save As — store the editor's current rows as a named view. */
export async function createSubsetView(request: PnlSaveSubsetViewRequest): Promise<PnlSubsetViewSummary> {
  return sendJson<PnlSubsetViewSummary>('POST', '/pnl-subset-views', request);
}

/** Save — overwrite an existing view. 404s rather than creating, so a stale id cannot make a surprise view. */
export async function updateSubsetView(viewId: number, request: PnlSaveSubsetViewRequest): Promise<PnlSubsetViewSummary> {
  return sendJson<PnlSubsetViewSummary>('PUT', `/pnl-subset-views/${viewId}`, request);
}

/** The views the caller may see for one hierarchy — metadata only, public first then their own. */
export async function listHierarchyViews(dimension: string, hierarchy: string, signal?: AbortSignal): Promise<{ views: PnlSubsetViewSummary[] }> {
  const path = `/pnl-dimensions/${encodeURIComponent(dimension)}/hierarchies/${encodeURIComponent(hierarchy)}/views`;
  return getJson<{ views: PnlSubsetViewSummary[] }>(path, signal);
}

/** One saved view with its rows. Fetched on selection — the list deliberately carries none. */
export async function getSubsetView(viewId: number, signal?: AbortSignal): Promise<PnlSubsetView> {
  return getJson<PnlSubsetView>(`/pnl-subset-views/${viewId}`, signal);
}

/** The aliases one hierarchy offers for member labels. */
export async function listHierarchyAliases(dimension: string, hierarchy: string, signal?: AbortSignal): Promise<PnlHierarchyAliases> {
  const path = `/pnl-dimensions/${encodeURIComponent(dimension)}/hierarchies/${encodeURIComponent(hierarchy)}/aliases`;
  return getJson<PnlHierarchyAliases>(path, signal);
}

/** The alias→label map for one hierarchy. Uncapped by design; the server logs its size. */
export async function getAliasLabels(dimension: string, hierarchy: string, attributeName: string, signal?: AbortSignal): Promise<PnlAliasLabels> {
  const path =
    `/pnl-dimensions/${encodeURIComponent(dimension)}/hierarchies/${encodeURIComponent(hierarchy)}` + `/aliases/${encodeURIComponent(attributeName)}`;
  return getJson<PnlAliasLabels>(path, signal);
}

/**
 * The same member tree addressed by `(dimension, hierarchy)`: a hierarchy with no registry entry has no
 * `hierarchy_id`, so the pair is what makes it reachable.
 */
export async function getHierarchyTreeByPair(dimension: string, hierarchy: string, signal?: AbortSignal): Promise<PnlHierarchyTree> {
  const path = `/pnl-dimensions/${encodeURIComponent(dimension)}/hierarchies/${encodeURIComponent(hierarchy)}/tree`;
  return getJson<PnlHierarchyTree>(path, signal, REPEAT_READ_MAX_AGE_MS);
}

/** Auth-gated: list the persisted PnL reports (for the Reports nav section). */
export async function listPnlReports(): Promise<PnlReportSummary[]> {
  return getJson<PnlReportSummary[]>('/pnl-reports', undefined, REPEAT_READ_MAX_AGE_MS);
}

/**
 * Auth-gated: every report row, including soft-deleted ones — the Manage Reports screen.
 * Deleted rows have to be reachable somewhere, or a soft delete is indistinguishable from a hard one.
 */
export async function listAllPnlReports(signal?: AbortSignal): Promise<PnlReportSummary[]> {
  return getJson<PnlReportSummary[]>('/pnl-reports?include_deleted=true', signal);
}

/** Auth-gated: hide a report from the pickers, or bring it back. Records the caller as the editor. */
export async function setPnlReportActivation(reportId: number, isActive: boolean): Promise<PnlReportSummary> {
  return sendJson<PnlReportSummary>('POST', `/pnl-reports/${reportId}/activation`, { is_active: isActive });
}

/** Auth-gated: the saved definition of a PnL report (for re-open / detail view). */
export async function getPnlReportConfig(reportId: number, signal?: AbortSignal, deckType?: string): Promise<PnlReportConfig> {
  const query = deckType ? `?${new URLSearchParams({ deck_type: deckType })}` : '';
  return getJson<PnlReportConfig>(`/pnl-reports/${reportId}/config${query}`, signal);
}

/** One deck's private configuration of a report, rejected when the response names another report or deck. */
export const getReportPrivateConfig = async (id: number, deck: string, signal?: AbortSignal): Promise<ReportPrivateConfigResponse> => {
  const config = await parseReportPrivateConfig(
    await apiRequest(`/pnl-reports/${id}/private-config?${new URLSearchParams({ deck_type: deck })}`, undefined, signal)
  );
  if (config.report_id !== id || config.deck_type !== deck) throw new Error('Unexpected report configuration identity');
  return config;
};

/** Auth-gated: soft-delete a report. */
export async function deletePnlReport(reportId: number): Promise<void> {
  return deleteResource(`/pnl-reports/${reportId}`);
}

// ─── Reports nav arrangement (per user, stored by the service) ─────────────────

const REPORT_NAV_PREFERENCE_PATH = '/report-nav/preference';

/** Auth-gated: how THIS caller arranged the Reports nav, plus the order every user starts from. */
export async function getReportNavPreference(signal?: AbortSignal): Promise<ReportNavPreferenceResponse> {
  return getJson<ReportNavPreferenceResponse>(REPORT_NAV_PREFERENCE_PATH, signal, REPEAT_READ_MAX_AGE_MS);
}

/** Auth-gated: replace this caller's arrangement. Whole-document — the nav sends the whole thing. */
export async function putReportNavPreference(preference: StarGridNavGroupPreference): Promise<ReportNavPreferenceResponse> {
  return sendJson<ReportNavPreferenceResponse>('PUT', REPORT_NAV_PREFERENCE_PATH, { preference });
}

/** Auth-gated: forget this caller's arrangement, putting them back on the admin or published default. */
export async function deleteReportNavPreference(): Promise<void> {
  return deleteResource(REPORT_NAV_PREFERENCE_PATH);
}

const REPORT_NAV_DEFAULT_PATH = '/report-nav/default';

/** Admin-only: replace the arrangement every user without their own sees. */
export async function putReportNavDefault(preference: StarGridNavGroupPreference): Promise<ReportNavPreferenceResponse> {
  return sendJson<ReportNavPreferenceResponse>('PUT', REPORT_NAV_DEFAULT_PATH, { preference });
}

/** Admin-only: drop the admin default, putting everyone without their own back on the published one. */
export async function deleteReportNavDefault(): Promise<void> {
  return deleteResource(REPORT_NAV_DEFAULT_PATH);
}

/** Available data versions, including the backend-provided Live entry. */
export const listPnlVersions = (onIncomplete?: () => void): Promise<PnlDataVersion[]> =>
  loadPnlVersions(
    (offset) => getJson<PnlDataVersion[]>(`/pnl-versions?limit=${PNL_VERSION_PAGE_SIZE}&offset=${offset}`, undefined, VERSIONS_MAX_AGE_MS),
    onIncomplete
  );
/** Available report definitions grouped by report family and deck type. */
export const listPnlDecks = (): Promise<PnlDeckGroup[]> => getJson<PnlDeckGroup[]>('/pnl-decks', undefined, CATALOG_MAX_AGE_MS);

// ─── Snapshot tags (Iceberg TAG refs on the alpha fact) ───────────────────────

/** Every snapshot of the alpha fact, tagged first. */
export async function listSnapshotTags(signal?: AbortSignal): Promise<SnapshotTagList> {
  return getJson<SnapshotTagList>('/pnl-snapshot-tags', signal);
}

/** Tag one snapshot; the server names it from the deck type, period and commit time. */
export async function createSnapshotTag(request: CreateSnapshotTagRequest): Promise<CreatedSnapshotTag> {
  return sendJson<CreatedSnapshotTag>('POST', '/pnl-snapshot-tags', request);
}
